import keys from '@debbie/common/dist/cache/keys.js';
import { lastDanishBankDayOfMonth } from '@debbie/common/dist/economy/helpers/dates/danish-bank-days.js';
import helpers from '@debbie/common/dist/economy/helpers/index.js';
import { Interval, isPreservedEntryStatus, replacementConfig, replacementDuration, replacementPreservedEntryIds, } from '@debbie/common/dist/economy/payment-plans/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { paymentPlans as paymentPlansApi } from '@debbie/cortex/dist/api/index.js';
import { useCaseConditions } from '@debbie/cortex/dist/case-conditions/index.js';
import { caseVouchersEntity } from '@debbie/cortex/dist/case-vouchers/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { useFixedDistributionsByCaseId } from '@debbie/cortex/dist/fixed-distributions/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { paymentPlansEntity } from '@debbie/cortex/dist/paymentPlans/index.js';
import q from '@debbie/cortex/dist/query.js';
import { useTenant } from '@debbie/cortex/dist/tenant/index.js';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../../theme.js';
import { errorNotification } from '../../../utils/errors.js';
import Button from '../../Button.js';
import DateInput from '../../DateInput.js';
import Modal from '../../Modal.js';
import { Section } from '../../PanelSection.js';
import IntervalInput from './IntervalInput.js';
import MethodInput from './MethodInput.js';
const EditPaymentPlanModal = (props) => {
    const { paymentPlan, caseVouchers, singleCase, onClose } = props;
    const lang = useSelector((s) => s.lang);
    const tenant = useTenant();
    const documentsLoader = useDocumentsByReference(singleCase.caseId);
    const documents = h.hasData(documentsLoader) ? documentsLoader.data : null;
    const creditorLoadable = useCreditor(singleCase.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const fixedDistributionsLoader = useFixedDistributionsByCaseId(singleCase.caseId);
    const fixedDistributions = h.hasData(fixedDistributionsLoader)
        ? fixedDistributionsLoader.data
        : null;
    const conditionsLoader = useCaseConditions(singleCase.caseId);
    const conditions = h.hasData(conditionsLoader) ? conditionsLoader.data : null;
    const preservedEntries = useMemo(() => paymentPlan.plan.filter((e) => isPreservedEntryStatus(e.status)), [paymentPlan.plan]);
    const firstReplaceableEntry = useMemo(() => paymentPlan.plan.find((e) => !isPreservedEntryStatus(e.status)), [paymentPlan.plan]);
    const initialMethod = paymentPlan.config?.method === 'amount' ? 'amount' : 'duration';
    const initialInterval = paymentPlan.config?.interval ?? Interval.LASTBANKDAYMONTH;
    const initialAmount = paymentPlan.config?.method === 'amount'
        ? paymentPlan.config.amount
        : firstReplaceableEntry?.amount || 0;
    const initialDuration = replacementDuration(paymentPlan);
    const defaultStartDate = moment(firstReplaceableEntry?.date ??
        moment().add(1, 'month').startOf('month').format());
    const initialScheduledAmountChanges = paymentPlan.config?.method === 'amount'
        ? (paymentPlan.config.scheduledAmountChanges ?? [])
            .filter((c) => moment(c.date).isAfter(firstReplaceableEntry?.date ?? moment().toISOString()))
            .map((c) => ({ amount: c.amount, date: moment(c.date) }))
        : [];
    const [byAmount, setByAmount] = useState(initialMethod === 'amount');
    const [amount, setAmount] = useState(initialAmount);
    const [amountRaw, setAmountRaw] = useState(initialAmount);
    const [duration, setDuration] = useState(initialDuration);
    const [durationRaw, setDurationRaw] = useState(String(initialDuration));
    const [interval, setInterval] = useState(initialInterval);
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [scheduledAmountChanges, setScheduledAmountChanges] = useState(initialScheduledAmountChanges);
    const [scheduledAmountChangesRaw, setScheduledAmountChangesRaw] = useState(initialScheduledAmountChanges.map((c) => c.amount));
    const snapStartDate = (d) => interval === Interval.LASTBANKDAYMONTH ? lastDanishBankDayOfMonth(d) : d;
    const isLastBankDayOfMonth = (date) => {
        const lastBankDay = lastDanishBankDayOfMonth(moment(date));
        return (date.getFullYear() === lastBankDay.year() &&
            date.getMonth() === lastBankDay.month() &&
            date.getDate() === lastBankDay.date());
    };
    const dateFilter = interval === Interval.LASTBANKDAYMONTH ? isLastBankDayOfMonth : undefined;
    const buildConfig = () => {
        if (byAmount) {
            const futureChanges = scheduledAmountChanges
                .filter((c) => c.date.isAfter(startDate))
                .map((c) => ({ amount: c.amount, date: c.date.toISOString() }));
            return replacementConfig(paymentPlan, {
                startDate: startDate.toISOString(),
                interval,
                method: 'amount',
                amount,
                scheduledAmountChanges: futureChanges.length > 0 ? futureChanges : undefined,
            });
        }
        return replacementConfig(paymentPlan, {
            startDate: startDate.toISOString(),
            interval,
            method: 'duration',
            duration,
        });
    };
    const editMutation = useMutation(async () => {
        if (h.isError(conditionsLoader)) {
            throw new Error(lang.costCeiling.failedToLoad);
        }
        if (tenant == null ||
            creditor == null ||
            documents == null ||
            fixedDistributions == null ||
            conditions == null) {
            throw new Error('Required data is still loading');
        }
        const config = buildConfig();
        if (!config) {
            throw new Error('Cannot edit payment plan without a config');
        }
        const newEntries = await helpers.paymentPlans.genReplacement(tenant, creditor, singleCase.economyEngine, JSON.parse(JSON.stringify(helpers.vouchers.postProcessing(caseVouchers, {
            includeIrrecoverableVouchers: false,
            includeDeletedVouchers: false,
            includeExpiredVouchers: false,
        }))), documents, fixedDistributions, paymentPlan, config, conditions);
        await paymentPlansApi.create(paymentPlan.caseId, {
            activationTrigger: false,
            plan: newEntries.map((entry) => ({
                amount: entry.amount,
                cancel: entry.cancel,
                deadline: entry.deadline,
                reminder: entry.reminder,
                date: entry.date,
                type: entry.type,
            })),
            config,
            preservedEntryIds: replacementPreservedEntryIds(paymentPlan),
        });
    }, {
        onSuccess: () => {
            q.invalidate([
                ...keys.write.modify(paymentPlansEntity, paymentPlan.paymentPlanId),
                ...keys.write.create(caseVouchersEntity),
            ]);
            onClose();
        },
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.paymentPlan);
        },
    });
    const isLoadingDeps = tenant == null ||
        creditor == null ||
        documents == null ||
        fixedDistributions == null;
    return (React.createElement(Modal, { onToggle: onClose, header: lang.paymentPlans.editPaymentPlan, maxWidth: "640px", footer: React.createElement(ModalFooter, null,
            React.createElement(Button, { theme: "light", onClick: onClose }, lang.paymentPlans.cancel),
            React.createElement(Button, { onClick: () => editMutation.mutate(), loading: editMutation.isLoading, disabled: isLoadingDeps }, lang.paymentPlans.save)) },
        React.createElement(Hint, null, lang.paymentPlans.editPaymentPlanHint.replace('{count}', String(preservedEntries.length))),
        React.createElement(Section, { header: lang.paymentPlans.calculationFoundation, description: lang.paymentPlans.calculationFoundationDescription },
            React.createElement(MethodInput, { byAmount: byAmount, setByAmount: setByAmount, amount: amount, setAmount: setAmount, amountRaw: amountRaw, setAmountRaw: setAmountRaw, duration: duration, setDuration: setDuration, durationRaw: durationRaw, setDurationRaw: setDurationRaw, scheduledAmountChanges: scheduledAmountChanges, setScheduledAmountChanges: setScheduledAmountChanges, scheduledAmountChangesRaw: scheduledAmountChangesRaw, setScheduledAmountChangesRaw: setScheduledAmountChangesRaw, startDate: startDate, snapStartDate: snapStartDate, dateFilter: dateFilter })),
        React.createElement(Section, { header: lang.paymentPlans.startDate, description: lang.paymentPlans.startDateDescription },
            React.createElement(DateInput, { placeholder: lang.paymentPlans.startDate, value: startDate, onChange: (d) => setStartDate(snapStartDate(d)), minDate: new Date(), filterDate: dateFilter, allowNull: false })),
        React.createElement(Section, { header: lang.paymentPlans.interval, description: lang.paymentPlans.intervalDescription },
            React.createElement(IntervalInput, { interval: interval, onChange: (next) => {
                    setInterval(next);
                    if (next === Interval.LASTBANKDAYMONTH) {
                        setStartDate((d) => lastDanishBankDayOfMonth(d));
                    }
                } }))));
};
export default EditPaymentPlanModal;
const Hint = styled.p `
  margin: 0 0 16px 0;
  padding: 10px 12px;
  background: ${THEME.ACCENT_SURFACE};
  border: 1px solid ${THEME.ACCENT_BORDER};
  border-radius: 8px;
  font-size: 13px;
  color: #444;
  line-height: 1.5;
`;
const ModalFooter = styled.div `
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;
//# sourceMappingURL=EditPaymentPlanModal.js.map